import { Tooltip } from "antd";

import { REVIEW_TYPE_LABEL } from "./lineage";
import type { LineageMergeEvidence } from "./lineage.types";

interface MergeEvidenceListProps {
  evidence: LineageMergeEvidence[];
}

function Metric({
  label,
  value,
  tip,
}: {
  label: string;
  value: string;
  tip: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <Tooltip title={tip}>
        <span className="w-fit cursor-help text-[10px] font-medium tracking-wide text-[#556C75] uppercase">
          {label}
        </span>
      </Tooltip>
      <span className="text-[13px] font-semibold text-[#2B4752] tabular-nums">
        {value}
      </span>
    </div>
  );
}

function EvidenceCard({ item }: { item: LineageMergeEvidence }) {
  const pct = (value: number | null) => (value === null ? "—" : `${value}%`);
  return (
    <div className="rounded-[8px] border border-[#E6EEF2] bg-white p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-mcqueenGrotesk text-[12px] font-bold text-[#2B4752]">
          {REVIEW_TYPE_LABEL[item.review_type] ?? item.review_type}
        </span>
        <span className="rounded bg-[#F1F5F9] px-1.5 py-0.5 text-[10px] font-semibold text-[#475569]">
          {item.status}
        </span>
        <span className="rounded bg-[#F1F5F9] px-1.5 py-0.5 text-[10px] font-semibold text-[#475569]">
          {item.priority} priority
        </span>
        {item.basket && (
          <Tooltip title="The rule basket that flagged this pair during PI deduplication.">
            <span className="cursor-help rounded bg-[#EEF4F8] px-1.5 py-0.5 text-[10px] font-semibold text-[#2B4752]">
              {item.basket}
            </span>
          </Tooltip>
        )}
      </div>

      <div className="mb-3 text-[12px] text-[#1F2933]">
        {item.counterpart_person_id ? (
          <span>
            {item.this_name ?? item.this_person_id}
            <span className="mx-1.5 text-[#556C75]">↔</span>
            {item.counterpart_name ?? item.counterpart_person_id}
            <Tooltip title="The other golden investigator in the candidate pair.">
              <span className="ml-1.5 cursor-help text-[11px] text-[#556C75]">
                {item.counterpart_person_id}
                {item.counterpart_city ? ` · ${item.counterpart_city}` : ""}
              </span>
            </Tooltip>
          </span>
        ) : (
          <Tooltip title="An over-cluster candidate flags this golden investigator as possibly two different people merged into one, so there is no counterpart record.">
            <span className="cursor-help">
              {item.this_name ?? item.this_person_id}
              <span className="ml-1.5 text-[11px] text-[#556C75]">
                flagged for splitting — no counterpart
              </span>
            </span>
          </Tooltip>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric
          label="Match confidence"
          value={pct(item.confidence_pct)}
          tip="Model confidence that the two golden investigators are the same person."
        />
        <Metric
          label="Name similarity"
          value={pct(item.name_similarity_pct)}
          tip="Textual similarity between the two canonical full names."
        />
        <Metric
          label="First-name similarity"
          value={pct(item.first_name_similarity_pct)}
          tip="Textual similarity between the two first names — the signal that most often separates a genuine duplicate from two relatives or colleagues at one site."
        />
        <Metric
          label="Model"
          value={item.model_version ?? "—"}
          tip="Version of the mastering model that scored this pair."
        />
      </div>

      {item.review_note && (
        <div className="mt-3 rounded border border-[#FDE68A] bg-[#FFFBEB] px-2 py-1.5 text-[12px] text-[#78350F]">
          {item.review_note}
        </div>
      )}
    </div>
  );
}

export default function MergeEvidenceList({
  evidence,
}: MergeEvidenceListProps) {
  return (
    <div className="flex flex-col gap-2">
      {evidence.map((item) => (
        <EvidenceCard key={item.review_id} item={item} />
      ))}
    </div>
  );
}
